import { FrVrfSpreadsheet } from "@vrf/fr-vrf-spreadsheet-react";
import { stringify } from "../../util/querystring-util";
import React, { useEffect, useMemo } from "react";
import { createPath, useNavigate } from "react-router-dom";
import {
  REQUEST_CD_TYPES,
  RootComponent,
  RootComponentListPageState,
  RootComponentPageMode,
  RootComponentSpreadsheetRow,
  SpreadsheetState,
  TEMPLATE_FILE_URL,
  requestCdTypes,
  usePageState,
} from ".";
import { useAppState } from "../../app";
import { ApplicationMessageScreen } from "../../messages/screen-message";

import {
  useGlobalNotifier,
  useMessage,
  useSpreadsheetState,
} from "@vrf/fr-vrf-core-react";
import { FrVrfButton, FrVrfFooter } from "@vrf/fr-vrf-web-components-react";
import { useGetGeneralCodes } from "../../services/data-list";
import {
  RootCpnt,
  UpdateRootComponentDeletionFlags,
  UpdateRootComponentsDeletionFlagsRequest,
  useGetRootComponents,
  useUpdateRootComponentsDeletionFlags,
} from "../../services/root-components";
import {
  INIT_ERROR_RESULT_DATA,
  mappingErrorResponseToSpreadsheet,
} from "../../util";
import {
  InDefCommonFlagType,
  TcktStsTypeCpntPrjPrgrs,
} from "../../util/general-code/enums/general-codes";
import { PJT_STS_LIST_PARAM_DELIMITER } from "../component-project-list/ComponentProjectListConstant";
import { usePageNavigation } from "../use-page-navigation";
import _ from "lodash";
import { Cell } from "@vrf/fr-vrf-spreadsheet-core-templating";

export const RootComponentListPageContent = () => {
  const displayColumn = [{ label: "Delete", value: "delFlg" }];
  const navigate = useNavigate();

  const transition = (path: string) => {
    const href = createPath({
      pathname: `${path}`,
    });
    navigate(href);
  };

  const { resetUserError, showToast, showConfirmationDialog, errorMessages } =
    useGlobalNotifier();
  const { messageForScreen } = useMessage();
  const { initUserError } = usePageNavigation();
  const [_appState, setAppState] = useAppState();
  const [pageState, setPageState] = usePageState();

  const [getGeneralCodesResponse, sendGetGeneralCodesRequest] =
    useGetGeneralCodes();
  const [getRootComponentsResponse, sendGetRootComponentsRequest] =
    useGetRootComponents();
  const [
    updateRootComponentsDeletionFlagsResponse,
    sendUpdateRootComponentsDeletionFlagsRequest,
  ] = useUpdateRootComponentsDeletionFlags();

  const { searchResult, dataList } = pageState as RootComponentListPageState;

  const onInitialDisplayed = () => {
    initUserError();
    setAppState((prevState) => ({ ...prevState, title: "RootComponent List" }));
    sendGetGeneralCodesRequest({ cdTypes: requestCdTypes });
    sendGetRootComponentsRequest({
      withCpntPjtStatusFlag: InDefCommonFlagType.YES,
    });
  };
  const onGetGeneralCodesRequest = () => {
    if (!getGeneralCodesResponse) return;
    const inDefCommonFlagType =
      getGeneralCodesResponse.gnrlCdTypeList.find(
        (e) => e.cdType === REQUEST_CD_TYPES.IN_DEF_COMMON_FLAG_TYPE,
      )?.cdList ?? [];

    setPageState((prevState) => ({
      ...prevState,
      dataList: {
        ...dataList,
        inDefCommonFlagType,
      },
    }));
  };

  const onGetRootComponentsRequestResponded = () => {
    if (!getRootComponentsResponse) return;
    const rootCpntListWithCpntExists =
      getRootComponentsResponse?.rootCpntList.map((rootCpnt) => {
        return {
          ...rootCpnt,
          cpntList: rootCpnt.cpntList.filter(
            (cpnt) => cpnt.delFlg === InDefCommonFlagType.NO,
          ),
        };
      });

    setPageState((prevState) => ({
      ...prevState,
      searchResult: {
        ...searchResult,
        rootCpntList: rootCpntListWithCpntExists ?? [],
      },
    }));
  };

  const onUpdateRootComponentsDeletionFlagsRequestResponded = () => {
    if (!updateRootComponentsDeletionFlagsResponse) return;
    if (Array.isArray(updateRootComponentsDeletionFlagsResponse)) {
      const copyRootCpntList = JSON.parse(
        JSON.stringify(spreadsheetState?.rootCpntList),
      ) as RootComponent[];

      const newSpreadsheetRecordData: RootComponent[] =
        mappingErrorResponseToSpreadsheet(
          updateRootComponentsDeletionFlagsResponse,
          copyRootCpntList,
          displayColumn,
        );

      if (!spreadsheetState) return;
      setSpreadsheetState({
        ...spreadsheetState,
        rootCpntList: newSpreadsheetRecordData,
      });
    } else {
      showToast({
        design: "completion",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00044),
      });
      sendGetRootComponentsRequest({
        withCpntPjtStatusFlag: InDefCommonFlagType.YES,
      });
    }
  };

  useEffect(onInitialDisplayed, []);
  useEffect(onGetGeneralCodesRequest, [getGeneralCodesResponse]);
  useEffect(onGetRootComponentsRequestResponded, [getRootComponentsResponse]);
  useEffect(onUpdateRootComponentsDeletionFlagsRequestResponded, [
    updateRootComponentsDeletionFlagsResponse,
  ]);

  const [spreadsheetState, setSpreadsheetState, initSpreadsheetState] =
    useSpreadsheetState<SpreadsheetState>();

  const spreadsheetInitialData: SpreadsheetState = useMemo(() => {
    return {
      dataList,
      rootCpntList: searchResult.rootCpntList.map((e) => ({
        ...INIT_ERROR_RESULT_DATA,
        isEdited: false,
        rootCpnt: e,
      })),
    };
  }, [searchResult.rootCpntList, dataList]);

  const changedRootComponentList = useMemo(() => {
    if (!spreadsheetState) return [];
    const rootCpntList: UpdateRootComponentDeletionFlags[] =
      spreadsheetState?.rootCpntList.flatMap((e, idx) => {
        if (e.isEdited) {
          return {
            index: idx,
            dipByRgn: e.rootCpnt.dipByRgn,
            rootCpntId: e.rootCpnt.rootCpntId,
            delFlg: e.rootCpnt.delFlg,
            updateDateTime: e.rootCpnt.updateDateTimeMRootCpnt,
          };
        }
        return [];
      });
    return rootCpntList;
  }, [spreadsheetState]);

  const isChangedRootComponentList = useMemo(
    () => changedRootComponentList.length > 0,
    [spreadsheetState?.rootCpntList],
  );

  const createDiscardDialog = (onOkButtonClicked: () => void) => {
    showConfirmationDialog({
      title: "",
      message: messageForScreen(ApplicationMessageScreen.APP_SCN_00033),
      type: "warning",
      onOkButtonClicked,
    });
  };

  const onCellClicked = (event: CustomEvent) => {
    const eventName = event.detail.name;
    const selectedRow: RootCpnt = event.detail.value;

    if (eventName === "rootCpntIdClicked" && selectedRow) {
      const navigationParameters = {
        dipByRgn: selectedRow.dipByRgn,
        rootCpntId: selectedRow.rootCpntId,
      };

      if (isChangedRootComponentList) {
        createDiscardDialog(() =>
          transition(`/component-list?${stringify(navigationParameters)}`),
        );
      } else {
        transition(`/component-list?${stringify(navigationParameters)}`);
      }
    }
    if (eventName === "rootCpntId_newTabIcon" && selectedRow) {
      const navigationParameters = {
        dipByRgn: selectedRow.dipByRgn,
        rootCpntId: selectedRow.rootCpntId,
      };
      const href = createPath({
        pathname: `/component-list?${stringify(navigationParameters)}`,
      });
      window.open(window.location.pathname + "#" + href);
    }
    if (eventName === "cmpProjectClicked" && selectedRow) {
      const navigationParameters = {
        dipByRgn: selectedRow.dipByRgn,
        rootCpntId: selectedRow.rootCpntId,
        cpntPjtSts:
          TcktStsTypeCpntPrjPrgrs.COMPONENT_PROJECT_REQUIREMENT_DESIGN +
          PJT_STS_LIST_PARAM_DELIMITER +
          TcktStsTypeCpntPrjPrgrs.COMPONENT_PROJECT_IN_PROGRESS,
      };

      if (isChangedRootComponentList) {
        createDiscardDialog(() =>
          transition(
            `/component-project-list?${stringify(navigationParameters)}`,
          ),
        );
      } else {
        transition(
          `/component-project-list?${stringify(navigationParameters)}`,
        );
      }
    }
    if (eventName === "cmpProject_newTabIcon" && selectedRow) {
      const navigationParameters = {
        dipByRgn: selectedRow.dipByRgn,
        rootCpntId: selectedRow.rootCpntId,
        cpntPjtSts:
          TcktStsTypeCpntPrjPrgrs.COMPONENT_PROJECT_REQUIREMENT_DESIGN +
          PJT_STS_LIST_PARAM_DELIMITER +
          TcktStsTypeCpntPrjPrgrs.COMPONENT_PROJECT_IN_PROGRESS,
      };
      const href = createPath({
        pathname: `/component-project-list?${stringify(navigationParameters)}`,
      });
      window.open(window.location.pathname + "#" + href);
    }
  };

  const onCellEdited = (event: CustomEvent) => {
    const eventName: string = event.detail.name;
    const value: string = event.detail.cell.value;
    const selectedRowIndex: number = event.detail.cell?.data.index;
    const isChanged: boolean = event.detail.cell.row.isChanged;

    if (!spreadsheetState) return;

    if (eventName === "delFlg") {
      const copyRootCpntList = JSON.parse(
        JSON.stringify(spreadsheetState?.rootCpntList),
      ) as RootComponent[];

      copyRootCpntList[selectedRowIndex].isEdited = isChanged;
      copyRootCpntList[selectedRowIndex].rootCpnt.delFlg = value;

      const newState = {
        ...spreadsheetState,
        rootCpntList: copyRootCpntList,
      };
      setSpreadsheetState(newState);
    }
  };

  const onCellBulkEdited = (event: CustomEvent) => {
    if (!spreadsheetState) return;
    const newRootComponentList = _.cloneDeep(
      spreadsheetState?.rootCpntList,
    ) as RootComponent[];

    event.detail.affectedCells.cells.forEach((item: Cell) => {
      const value: string = item.value;
      const rowIdx: number = item.data.index;

      const isChanged: boolean = item.row.isChanged;
      newRootComponentList[rowIdx].rootCpnt.delFlg = value;
      newRootComponentList[rowIdx].isEdited = isChanged;
    });

    setSpreadsheetState({
      ...spreadsheetState,
      rootCpntList: newRootComponentList,
    });
  };

  const validate = (name: string) => {
    if (!spreadsheetState) return true;
    if (name === "delFlg") {
      const hasBlankDelFlg = spreadsheetState.rootCpntList.some(
        (e) =>
          e.rootCpnt?.delFlg === null ||
          e.rootCpnt?.delFlg === undefined ||
          e.rootCpnt?.delFlg === "",
      );

      if (!isChangedRootComponentList || hasBlankDelFlg) {
        return true;
      }
      return false;
    }
  };

  /**
   * Footer
   */
  const FooterContent: React.FC = () => {
    const clickedSaveHandle = () => {
      resetUserError();
      if (!spreadsheetState) return;
      const sendData: UpdateRootComponentsDeletionFlagsRequest = {
        rootCpntList: changedRootComponentList,
      };
      sendUpdateRootComponentsDeletionFlagsRequest(sendData);
    };

    const onSaveConfirmDialogHandle = () => {
      showConfirmationDialog({
        title: "",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00045),
        type: "confirm",
        onOkButtonClicked: clickedSaveHandle,
      });
    };

    const clickedAddRootComponentHandle = () => {
      const navigationParameters = {
        mode: RootComponentPageMode.ADD_ROOT_COMPONENT,
      };

      if (isChangedRootComponentList) {
        createDiscardDialog(() =>
          transition(`/component-addition?${stringify(navigationParameters)}`),
        );
      } else {
        transition(`/component-addition?${stringify(navigationParameters)}`);
      }
    };

    return (
      <FrVrfFooter>
        <FrVrfButton
          content="Add Root Component & Component"
          size="large"
          onClicked={clickedAddRootComponentHandle}
          authActId="root_component_list.button.add_root_component"
        />
        <FrVrfButton
          content="Save"
          size="large"
          onClicked={onSaveConfirmDialogHandle}
          disabled={Boolean(validate("delFlg"))}
          style={{ marginRight: "37px" }}
          authActId="root_component_list.button.save"
        />
      </FrVrfFooter>
    );
  };

  return (
    <>
      <FrVrfSpreadsheet
        data={spreadsheetInitialData}
        templateUrl={TEMPLATE_FILE_URL}
        onInitialized={initSpreadsheetState}
        onCellClicked={onCellClicked}
        onCellEdited={onCellEdited}
        onCellBulkEdited={onCellBulkEdited}
        maxHeight={740}
      />
      <FooterContent />
    </>
  );
};
